import ComponentBase from '../base/component-base';
import {
  EVENT_GLOBAL_CART_ACTIVATE,
  EVENT_GLOBAL_CART_DEACTIVATE,
  EVENT_GLOBAL_UPDATE_CART,
  EVENT_CART_CONTENT_LOADED,
  EVENT_CART_OPERATION_START,
  EVENT_CART_OPERATION_END,
} from '../../constants/event';
import Cart from '../../libs/cart';
import ScrollLock from '../../libs/scroll-lock';

/**
 * Cart Modal Component
 * Manages the global cart modal functionality
 * Only one instance should exist on the page
 */
class CartModal extends ComponentBase {
  private closeButton: HTMLButtonElement | null = null;
  private cartItemsContainer: HTMLElement | null = null;
  private cartTotalValue: HTMLElement | null = null;
  private cartSection: HTMLElement | null = null;
  private spriteUrl: string = '';
  private onActivateBinding: () => void;
  private onDeactivateBinding: () => void;
  private onUpdateCartBinding: (e: Event) => void;
  private onCloseClickBinding: () => void;
  private onBackdropClickBinding: (e: MouseEvent) => void;
  private onCartOperationStartBinding: () => void;
  private onCartOperationEndBinding: () => void;

  constructor(element: HTMLElement) {
    super(element);

    console.log('[CartModal] Initializing cart modal:', element);

    // Bind event handlers
    this.onActivateBinding = this.onActivate.bind(this);
    this.onDeactivateBinding = this.onDeactivate.bind(this);
    this.onUpdateCartBinding = this.onUpdateCart.bind(this);
    this.onCloseClickBinding = this.onCloseClick.bind(this);
    this.onBackdropClickBinding = this.onBackdropClick.bind(this);
    this.onCartOperationStartBinding = this.onCartOperationStart.bind(this);
    this.onCartOperationEndBinding = this.onCartOperationEnd.bind(this);

    this.init();
  }

  protected init(): void {
    // Find child elements
    this.closeButton = this.element.querySelector('.c-cart-modal__close-button');
    this.cartItemsContainer = this.element.querySelector('.c-cart-items');
    this.cartTotalValue = this.element.querySelector('.c-cart-section__total-value');
    this.cartSection = this.element.querySelector('.js-cart-section');

    // Get sprite URL from existing SVG use element
    const existingUseElement = this.closeButton?.querySelector('use');
    if (existingUseElement) {
      const href = existingUseElement.getAttribute('href') || '';
      // Extract the base URL (everything before the #)
      const hashIndex = href.indexOf('#');
      if (hashIndex !== -1) {
        this.spriteUrl = href.substring(0, hashIndex);
      }
    }

    console.log('[CartModal] Elements:', {
      closeButton: this.closeButton,
      cartItemsContainer: this.cartItemsContainer,
      cartTotalValue: this.cartTotalValue,
      cartSection: this.cartSection,
      spriteUrl: this.spriteUrl,
    });

    // Add event listeners
    document.addEventListener(EVENT_GLOBAL_CART_ACTIVATE, this.onActivateBinding);
    document.addEventListener(EVENT_GLOBAL_CART_DEACTIVATE, this.onDeactivateBinding);
    document.addEventListener(EVENT_GLOBAL_UPDATE_CART, this.onUpdateCartBinding);
    document.addEventListener(EVENT_CART_OPERATION_START, this.onCartOperationStartBinding);
    document.addEventListener(EVENT_CART_OPERATION_END, this.onCartOperationEndBinding);

    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.onCloseClickBinding);
    }

    // Close on backdrop click
    this.element.addEventListener('click', this.onBackdropClickBinding);

    console.log('[CartModal] Event listeners attached');
  }

  private async onActivate(): Promise<void> {
    console.log('[CartModal] Activating modal');

    // Update cart content before showing modal
    await this.updateCartContent();

    // Show modal after content is loaded
    this.element.classList.add('is-active');
    document.body.classList.add('cart-modal-open');

    // Lock scroll
    ScrollLock.getInstance().disable();
  }

  private async onDeactivate(): Promise<void> {
    this.element.classList.remove('is-active');
    document.body.classList.remove('cart-modal-open');

    // Unlock scroll
    await ScrollLock.getInstance().enable();
  }

  private onCloseClick(): void {
    document.dispatchEvent(new CustomEvent(EVENT_GLOBAL_CART_DEACTIVATE));
  }

  private onBackdropClick(e: MouseEvent): void {
    // Close only if clicking on the backdrop (not on the modal content)
    if (e.target === this.element) {
      this.onCloseClick();
    }
  }

  private async onUpdateCart(e: Event): Promise<void> {
    const customEvent = e as CustomEvent;
    const { count } = customEvent.detail;

    console.log('[CartModal] Cart updated, loading cart content...');

    // Update cart content
    await this.updateCartContent();

    // Dispatch event to signal cart content is loaded
    console.log('[CartModal] Cart content loaded, dispatching EVENT_CART_CONTENT_LOADED');
    document.dispatchEvent(new CustomEvent(EVENT_CART_CONTENT_LOADED));
  }

  private async updateCartContent(): Promise<void> {
    try {
      console.log('[CartModal] Fetching cart data...');
      const cartData = await Cart.get();

      console.log('[CartModal] Cart data received:', cartData);

      // Update cart items
      if (this.cartItemsContainer) {
        this.renderCartItems(cartData.items);
      }

      // Update total
      if (this.cartTotalValue) {
        this.cartTotalValue.textContent = this.formatPrice(cartData.total_price);
      }

      console.log('[CartModal] Cart content updated');
    } catch (error) {
      console.error('[CartModal] Error updating cart content:', error);
    }
  }

  private renderCartItems(items: any[]): void {
    if (!this.cartItemsContainer) {
      return;
    }

    if (this.cartSection) {
      this.cartSection.classList.toggle('is-empty', items.length === 0);
    }

    if (items.length === 0) {
      this.cartItemsContainer.innerHTML = '<li class="c-cart-items__empty">カートは空です</li>';
      return;
    }

    const itemsHTML = items
      .map((item) => this.renderCartItem(item))
      .join('');

    this.cartItemsContainer.innerHTML = itemsHTML;

    // Add event listeners to item action buttons
    this.attachItemEventListeners();
  }

  private renderCartItem(item: any): string {
    return `
      <li class="c-cart-items__item">
        <div class="c-cart-item">
          <div class="c-cart-item__image">
            <img
              src="${item.featured_image?.url || ''}"
              width="100"
              height="100"
              loading="lazy"
              alt="${item.product_title}"
            >
          </div>
          <div class="c-cart-item__info">
            <p class="c-cart-item__name">${item.product_title}</p>
            <p class="c-cart-item__price">
              <span class="c-cart-item__price-value">${this.formatPrice(item.final_price)}（tax in）</span>
              <span class="c-cart-item__price-quantity">×${item.quantity}</span>
            </p>
          </div>
          <div class="c-cart-item__actions">
            <button class="c-cart-item__add-button" data-variant-id="${item.variant_id}" data-quantity="${item.quantity}">
              <svg focusable="false" role="img" width="12" height="12">
                <title>追加する</title>
                <use href="${this.spriteUrl}#global-plus"></use>
              </svg>
            </button>
            <button class="c-cart-item__minus-button" data-variant-id="${item.variant_id}" data-quantity="${item.quantity}">
              <svg focusable="false" role="img" width="12" height="12">
                <title>減らす</title>
                <use href="${this.spriteUrl}#global-minus"></use>
              </svg>
            </button>
            <button class="c-cart-item__delete-button" data-variant-id="${item.variant_id}">
              <svg focusable="false" role="img" width="14" height="14">
                <title>削除する</title>
                <use href="${this.spriteUrl}#global-trash"></use>
              </svg>
            </button>
          </div>
        </div>
      </li>
    `;
  }

  private attachItemEventListeners(): void {
    if (!this.cartItemsContainer) {
      return;
    }

    // Add buttons
    const addButtons = this.cartItemsContainer.querySelectorAll('.c-cart-item__add-button');
    addButtons.forEach((button) => {
      button.addEventListener('click', async (e) => {
        const target = e.currentTarget as HTMLButtonElement;
        const variantId = target.dataset.variantId;
        const currentQuantity = parseInt(target.dataset.quantity || '1', 10);

        if (variantId) {
          await Cart.update({ id: variantId, quantity: currentQuantity + 1 });
        }
      });
    });

    // Minus buttons
    const minusButtons = this.cartItemsContainer.querySelectorAll('.c-cart-item__minus-button');
    minusButtons.forEach((button) => {
      button.addEventListener('click', async (e) => {
        const target = e.currentTarget as HTMLButtonElement;
        const variantId = target.dataset.variantId;
        const currentQuantity = parseInt(target.dataset.quantity || '1', 10);

        if (variantId) {
          if (currentQuantity === 1) {
            // Delete item when quantity is 1
            await Cart.remove({ id: variantId });
          } else {
            // Decrease quantity
            await Cart.update({ id: variantId, quantity: currentQuantity - 1 });
          }
        }
      });
    });

    // Delete buttons
    const deleteButtons = this.cartItemsContainer.querySelectorAll('.c-cart-item__delete-button');
    deleteButtons.forEach((button) => {
      button.addEventListener('click', async (e) => {
        const target = e.currentTarget as HTMLButtonElement;
        const variantId = target.dataset.variantId;

        if (variantId) {
          await Cart.remove({ id: variantId });
        }
      });
    });
  }

  private formatPrice(price: number): string {
    return `¥${(price / 100).toLocaleString('ja-JP')}`;
  }

  private onCartOperationStart(): void {
    console.log('[CartModal] Cart operation started');
    if (this.cartSection) {
      this.cartSection.classList.add('is-loading');
    }
  }

  private onCartOperationEnd(): void {
    console.log('[CartModal] Cart operation ended');
    if (this.cartSection) {
      this.cartSection.classList.remove('is-loading');
    }
  }

  protected onDestroy(): void {
    super.onDestroy();

    document.removeEventListener(EVENT_GLOBAL_CART_ACTIVATE, this.onActivateBinding);
    document.removeEventListener(EVENT_GLOBAL_CART_DEACTIVATE, this.onDeactivateBinding);
    document.removeEventListener(EVENT_GLOBAL_UPDATE_CART, this.onUpdateCartBinding);
    document.removeEventListener(EVENT_CART_OPERATION_START, this.onCartOperationStartBinding);
    document.removeEventListener(EVENT_CART_OPERATION_END, this.onCartOperationEndBinding);

    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.onCloseClickBinding);
    }

    this.element.removeEventListener('click', this.onBackdropClickBinding);
  }
}

export default CartModal;
